/* tslint:disable:import-spacing no-inferrable-types variable-name align no-trailing-whitespace */
import {
  TrainClass,
  TrainDirection
}                          from './train.class';
import { Track }           from './track';
import { Stop }            from './stop';
import { DecisionPoint }   from './decision.point';
import { MovementHistory } from './movement.history';

export enum TrainType {
  ACTUAL      = 0,
  EVEN        = 1,
  EXPONENTIAL = 2,
  SCHEDULED   = 4,
  WEEKLY      = 5,
  NONE        = 6
}

/**
 *
 * @class Train
 *
 * @brief base class for the different kind of trains in our model.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2019
 *
 */
export class Train {

  /** @var _id unique id for this train */
  public            _id: number               = -1;

  /** @var _name human readable label for ths train */
  public          _name: string               = '';

  /** @var _paths the list of [to,from] tuples for this train */
  public         _paths: (Track|null)[][]     = [];

  /** @var _tc the train category */
  public            _tc: TrainClass|null      = null;

  /** @var _tonnage (short tons) weight */
  public       _tonnage: number               = 0.0;

  /** @var _length (feet) */
  public        _length: number               = 0.0;

  /** @var _priority scheduling priority */
  public      _priority: number               = 0.0;

  /** @var _overtake maximum overtake delay */
  public      _overtake: number               = 0.0;

  /** @var _nth the zero based index for this item */
  public           _nth: number               = -1;

  /** @var _type the kind of train */
  public          _type: TrainType            = TrainType.ACTUAL;

  /** @var _generatedId the unique id for the train in the simulation */
  public   _generatedId: number               = -1;

  /** @var _generatedName the unique name for the train in the simulation */
  public _generatedName: string               = '';

  /** @var _tag simple but unique tag for displaying train name uniquely */
  public           _tag: string               = '';

  /** @var _stops the places where the train stops for activity */
  public         _stops: Stop[]               = [];

  public       _history: MovementHistory|null = null;

  /** from most previous call to validate() these are the fields that have invalid value. */
  public _invalidFields: string[]             = [];

  /** errors found in validation */
  public _errors: string[]                    = [];

  /** from most previous call to validate() these are the fields that have invalid value. */
  public _warningFields: string[]             = [];

  /** errors found in validation */
  public _warnings: string[]                  = [];


  /** the map of which decision points for this train have a positive work block related flag */
  public _workFlags: Map<number, [boolean, boolean]> = new Map<number, [boolean, boolean]>();

  /** @brief convert track type to a string */
  public static typeToString(type: TrainType): string {

    switch (type) {
      case TrainType.ACTUAL:      return 'act';
      case TrainType.EVEN:        return 'evn';
      case TrainType.EXPONENTIAL: return 'exp';
      case TrainType.SCHEDULED:   return 'sch';
      case TrainType.WEEKLY:      return 'wky';
    }

    return '';
  }

  /** for a given decision point (by id), set the commited to workblock and holding for work block flags. */
  public setWorkBlockFlags(dpId: number, cw: boolean, hw: boolean): void {

    this._workFlags.set(dpId, [cw, hw]);
  }

  public getWorkBlockFlags(dpId: number): [boolean, boolean] {

    return this._workFlags.get(dpId) ?? [false, false];
  }

  /** @brief fetch the unique train id/tag in a format that is the same as the line simulator would use */
  public static iid(train: Train): string {

    if (!train._tc) {

      return '';
    }

    return `TRN-${Train.typeToString(train._type).toUpperCase()}-${train._generatedId}-${TrainClass.typeToString(train._tc._dir)}`;
  }

  /** @brief convert track string to a type */
  public static typeFromString(type: string): TrainType {

    const tmp: string = type.toLowerCase().trim();

    if (tmp === 'act') {

      return TrainType.ACTUAL;

    } else if (tmp === 'evn') {

      return TrainType.EVEN;

    } else if (tmp === 'exp') {

      return TrainType.EXPONENTIAL;

    } else if (tmp === 'sch') {

      return TrainType.SCHEDULED;

    } else if (tmp === 'wky') {

      return TrainType.WEEKLY;

    }

    return TrainType.NONE;
  }

  /** @brief standard constructor */
  constructor(        id: number,
                    name: string,
                   paths: (Track|null)[][],
              trainClass: TrainClass|null,
                 tonnage: number,
                  length: number,
                priority: number,
                overtake: number,
                     nth: number) {

    this._id       = id;
    this._name     = name;
    this._paths    = paths;
    this._tc       = trainClass;
    this._tonnage  = tonnage;
    this._length   = length;
    this._priority = priority;
    this._overtake = overtake;
    this._nth      = nth;

    this._history  = new MovementHistory(this);
  }

  /** @brief helper to make a new instance of this template */
  public clone(): Train {

    let newTrain: Train = new Train(
      this._id,
      this._name,
      this._paths,
      this._tc,
      this._tonnage,
      this._length,
      this._priority,
      this._overtake,
      -1
    );

    newTrain._type      = this._type;
    newTrain._tag       = this._tag;
    newTrain._stops     = this._stops;
    newTrain._workFlags = this._workFlags;

    return newTrain;
  }

  /** @brief set the generated id */
  public setGeneratedID(id: number) {

    this._tag         = Train.typeToString(this._type) + '-' + id + ':' + this._name;

    this._generatedId = id;
  }

  /** @brief fetch the generated id */
  public generatedID(): number {

    return this._generatedId;
  }

  /** @brief set the generated name */
  public setGeneratedName(name: string) {

    this._generatedName = name;
  }

  /** @brief fetch the generated name */
  public generatedName(): string {

    return this._generatedName;
  }

  /** @brief fetch the decision point the train starts from */
  public startDecisionPoint(): DecisionPoint|null {

    if (!this._paths[0]) {

      /* this train was likely missing its origin or destination link in the input JSON */

      return null;
    }

    const    src: Track|null             = this._paths[0][0];

    if (!src) {

      return null;
    }

    const  check: (DecisionPoint|null)[] = [src._after, src._before];
    let   result: DecisionPoint|null     = null;

    for (const dp of check) {

      if (!dp) {

        continue;
      }

      if (!result) {

        result = dp;

        continue;
      }

      /* we want the 'least' mileage the train can have, for its direction */

      if (this._tc && this._tc._dir === TrainDirection.EAST) {

        /* 0 is the least */

        if (dp._mileage < result._mileage) {

          result = dp;

        }

      } else {

        /* max distance is the least */

        if (dp._mileage > result._mileage) {

          result = dp;

        }
      }

    }

    return result;
  }


  /** @brief fetch the decision point the train ends at */
  public endDecisionPoint(): DecisionPoint|null {

    if (!this._paths[0]) {

      /* this train was likely missing its origin or destination link in the input JSON */

      return null;
    }

    const    dst: Track|null             = this._paths[0][1];

    if (!dst)  {

      return null;
    }

    const  check: (DecisionPoint|null)[] = [dst._after, dst._before];
    let   result: DecisionPoint|null     = null;

    for (const dp of check) {

      if (!dp) {

        continue;
      }

      if (!result) {

        result = dp;

        continue;
      }

      /* we want the 'most' mileage the train can have, for its direction */

      if (this._tc && this._tc._dir === TrainDirection.EAST) {

        /* 0 is the least */

        if (dp._mileage > result._mileage) {

          result = dp;

        }

      } else {

        /* 0 distance is the least */

        if (dp._mileage < result._mileage) {

          result = dp;

        }
      }

    }

    return result;
  }

  /** set the origin */
  public setStartTrack(track: Track) {

    /*
     * SN-1621 - we can't wipe out the paths, we have to "merge in" otherwise we create
     * an error, because any validation after this will fail, because the "other" side has
     * now disappeared.  we don't have to do anything fancy here; currently we always use
     * the first path no matter what.  So just replace this part of the first path.
     *
     */

    if ((this._paths === null) || (typeof this._paths === 'undefined')) {

      this._paths = [];

      this._paths.push([null, null]);
    }

    this._paths[0][0] = track;
  }

  /** set the destination */
  public setDestinationTrack(track: Track) {

    /*
     * SN-1621 - we can't wipe out the paths, we have to "merge in" otherwise we create
     * an error, because any validation after this will fail, because the "other" side has
     * now disappeared.  we don't have to do anything fancy here; currently we always use
     * the first path no matter what.  So just replace this part of the first path.
     *
     */

    if ((this._paths === null) || (typeof this._paths === 'undefined')) {

      this._paths = [];

      this._paths.push([null, null]);
    }

    this._paths[0][1] = track;
  }

  /** @brief helper to get a nice label for the train */
  public quickName(): string {

    let text: string = this._name.trim().toUpperCase();

    text += ' (' + Train.typeToString(this._type) + '-' + this._generatedId + ')';

    let dir: string = this._tc ? TrainClass.typeToString(this._tc._dir) : '';

    if (dir === 'E') {

      dir = 'EAST';

    } else {

      dir = 'WEST';
    }

    text += ' ' + dir;

    return text;
  }

  /** return true if the given value is a compatible type for the given field (for this train) */
  public checkType(field: string, value: any): boolean {

    let f: string|null = this.normalizeFieldName(field);

    if (!f) {

      return false;
    }

    /* the basic fields that most train types have ...  */

    if ((f === 'name') && (typeof value === 'string')) {

      return true;
    }

    if ((f === 'tonnage') ||
        (f === 'length') ||
        (f === 'priority') ||
        (f === 'overtake')) {

      let n: number = parseFloat(value);

      return !isNaN(n);
    }

    if (f === 'trainclass') {

      return value instanceof TrainClass;
    }

    /* departure is # of seconds offset (from sim start,  week start, day start etc. */

    if (f === 'departure') {

      let n: number = parseFloat(value);

      return !isNaN(n);
    }

    /* seed is a random integer */

    if (f === 'seed') {

      let n: number = parseInt(value);

      return !isNaN(n);
    }

    /* daily is # trains per day */

    if (f === 'daily') {

      let n: number = parseInt(value);

      return !isNaN(n);
    }

    /* days is a string (, list) of day numbers where the train can run */

    if ((f === 'days') && (typeof value === 'string')) {

      return true;
    }

    /* early/late percent/stddev tuning... */

    if ((f === 'percentearly') ||
        (f === 'early') ||
        (f === 'late')) {

      let n: number = parseFloat(value);

      return !isNaN(n);
    }

    if ((f === 'distribution') && (typeof value === 'string')) {

      return true;
    }

    if (f === 'weekly') {

      let n: number = parseFloat(value);

      return !isNaN(n);
    }

    if ((f === 'src') && (value instanceof Track)) {

      return true;
    }

    return (f === 'dst') && (value instanceof Track);
  }

  /** map the given field to a known field name if possible.  Returns null if unrecognized. */
  public normalizeFieldName(field: string): string|null {

    field = field.trim().toLowerCase();

    if (['train', 'trainname', 'name'].includes(field)) {

      return 'name'
    }

    if (['tc', 'trainclass', 'class', 'cls'].includes(field)) {

      return 'trainclass'
    }

    if (['len', 'trainlength', 'l', 'length'].includes(field)) {

      return 'length'
    }

    if (['shorttons', 'tons', 't', 'tonnage'].includes(field)) {

      return 'tonnage'
    }

    if (['tp', 'pri', 'p', 'priority'].includes(field)) {

      return 'priority'
    }

    if (['ot', 'maxdelay', 'overtake'].includes(field)) {

      return 'overtake'
    }

    if (['dep', 'departure'].includes(field)) {

      return 'departure'
    }

    if (['s', 'seed'].includes(field)) {

      return 'seed'
    }

    if (['daily', 'perday'].includes(field)) {

      return 'daily'
    }

    if (['dayofweek', 'days'].includes(field)) {

      return 'days'
    }

    if (['percentearly', 'days'].includes(field)) {

      return 'percentearly'
    }

    if (['stddev',  'stddevearly', 'early'].includes(field)) {

      return 'early'
    }

    if (['stddevlate', 'late'].includes(field)) {

      return 'late'
    }

    if (['weekly', 'wkly'].includes(field)) {

      return 'weekly'
    }

    if (['distribution', 'dist'].includes(field)) {

      return 'distribution'
    }

    if (['source', 'src'].includes(field)) {

      return 'src'
    }

    if (['dest','destination','dst'].includes(field)) {

      return 'dst'
    }

    return null;
  }

  /** subclasses must indicate which fields can be edited */
  public canEdit(field: string): boolean|null {

    let f: string|null = this.normalizeFieldName(field);

    if (!f) {

      return null;
    }

    if ((f === 'src') || (f === 'dst')) {

      return true;
    }

    if (this._type === TrainType.ACTUAL) {

      /* has overtake and departure */

      if (['overtake', 'departure'].includes(f)) {

        return true;
      }

    } else if (this._type === TrainType.EVEN) {

      if (['overtake', 'departure', 'seed', 'early', 'daily'].includes(f)) {

        return true;
      }

    } else if (this._type === TrainType.EXPONENTIAL) {

      if (['overtake', 'seed', 'daily'].includes(f)) {

        return true;
      }

    } else if (this._type === TrainType.SCHEDULED) {

      if (['overtake', 'seed', 'days', 'percentearly', 'early', 'late', 'departure'].includes(f)) {

        return true;
      }

    } else if (this._type === TrainType.WEEKLY) {

      if (['overtake', 'weekly', 'seed', 'distribution'].includes(f)) {

        return true;
      }

    }

    /* all trains can edit name, train class, length, tonnage, priority */

    return ['name', 'trainclass', 'length', 'tonnage', 'priority', 'overtake'].includes(f);
  }

  /** normalize the field name and at the same time check for type compatibility.  If failure, return '' (empty) */
  public normalizeAndCheck(field: string, value: any): string {

    if (!this.checkType(field, value)) {

      /* incompatible type for field */

      return '';
    }

    let f: string|null = this.normalizeFieldName(field);

    if (!f) {

      /* no such field in any train type */

      return '';
    }

    if (!this.canEdit(f)) {

      return '';
    }

    return f;
  }

  /** when editing fails because the value given was entirely unusable, fill in a default but still bad value */
  public defaultBadValue(field:  string): void {

    /*
     * this method is required because they may give a value like "undefined" which can't
     * match any of our types, it can't be "set" as the value.  In cases like that, we fill
     * in a typed value that does match the type of the field, but its still a bad value,
     * to reflect that the edit is invalid.
     *
     */

    switch (field) {

      case 'name':       this._name     = '';   break;
      case 'trainclass': this._tc       = null; break;
      case 'length':     this._length   = -1;   break;
      case 'tonnage':    this._tonnage  = -1;   break;
      case 'priority':   this._priority = -1;   break;
      case 'overtake':   this._overtake = -1;   break
    }

  }

  /* before editing, translate/cast the value we're about to set (if required) */
  public castValue(field: string, value: any): any {

    /* normally just use the value given */

    if (field === 'seed') {

      return parseInt(value);
    }

    return value;
  }

  /** before we edit a field, cast the value and handle any severe errors */
  public prepare(field: string, value: any): {field: string, value: any} {

    /* lookup proper name, check type of value etc. */

    const check: string = this.normalizeAndCheck(field, value);

    if (check === '') {

      /* if we failed basic checks, place this field in error, because editing of it failed. */

      this.defaultBadValue(field);

    }

    value = this.castValue(field, value);

    return {field: check, value};
  }

  /** edit this field on this train (if applicable), return true if edit succeeded */
  public edit(field: string, value: any): boolean {

    /* prepare for editing... */

    const param:  {field: string, value: any} = this.prepare(field, value);

    /* did they modify the trip end points? */

    if (param.field === 'src') {

      this.setStartTrack(value);

      return this.validate();

    } else if (param.field === 'dst') {

      this.setDestinationTrack(value);

      return this.validate();
    }

    /* normal edit ... */

    switch (param.field) {

      case 'name':       this._name     = param.value as string;     return this.validate();
      case 'trainclass': this._tc       = param.value as TrainClass; return this.validate();
      case 'length':     this._length   = param.value as number;     return this.validate();
      case 'tonnage':    this._tonnage  = param.value as number;     return this.validate();
      case 'priority':   this._priority = param.value as number;     return this.validate();
      case 'overtake':   this._overtake = param.value as number;     return this.validate();
    }

    return this.validate();
  }

  /** helper to confirm its like a positive number */
  public isPositiveWholeNumber(value: any): boolean {

    value = parseFloat(value);

    if (isNaN(value)) {

      return false;
    }

    return value > 0;
  }

  /** helper to confirm its like a positive number */
  public isPositiveNumber(value: any): boolean {

    value = parseFloat(value);

    if (isNaN(value)) {

      return false;
    }

    return value >= 0;
  }

  /** confirm that the src and dst tracks are in the right order for the current train direction */
  public checkDirection(): string {

    if (this._paths[0] === undefined) {

      this.error('src,dst', `The start and end points must be selected.`);

      return `Start and End must both be defined.`;

    } else {

      const src: Track|null = this._paths[0][0];
      const dst: Track|null = this._paths[0][1];

      if (!(src instanceof Track) || !(dst instanceof Track)) {

        this.error('src,dst', `Both start and end points must be selected.`);

        return `Start and End must both be defined.`;

      } else {

        /* make sure start and end are ordered correctly */

        if (!this._invalidFields.includes('src') && !this._invalidFields.includes('dst')) {

          if (this._tc && this._tc._dir === TrainDirection.EAST) {

            /* eastbound, end mileage must be lower */

            if (dst.maxMiles() >= src.maxMiles()) {

              this.error('src,dst', `Eastbound, must have destination with less mileage.`);

              return `Start and End are backwards for train direction.`;
            }

          } else {

            /* westbound, end mileage must be higher */

            if (dst.maxMiles() <= src.maxMiles()) {

              this.error('src,dst', `Westbound, must have destination with more mileage.`);

              return `Start and End are backwards for train direction.`;
            }
          }

        } else {

          return `Start or End not set, or in wrong order.`;
        }
      }
    }

    return ``;
  }

  /**
   * sometimes errors, are not absolutely critical errors, they are really just
   * warnings, or at least a kind of error we consider suppressible. Each kind of
   * train generates its own errors through its own validation, so we must allow
   * each one to define its own kind of suppression.
   *
   * @param field string the field we had an error on
   * @param error string the error for that field.
   *
   * @return boolean true means the error can be suppressed or considered a warning.
   */
  public suppressible(field: string, error: string): boolean {

    /* by default, errors are errors :) */

    return false;
  }

  /** check if this value *would* be valid for this field */
  public checkValue(field: string, value: any): string {

    let f: string|null = this.normalizeFieldName(field);

    if (!f) {

      return '';
    }

    switch (f) {

      case 'name': {

          if ((value === '') || (typeof value !== 'string')) {

            return `Name can not be empty.`;
          }
        }
        break;

      case 'trainclass': {

          if ((value === null) || !(value instanceof TrainClass)) {

            return `Train Class can not be empty.`;
          }

          return this.checkDirection();
        }


      case 'length':     {

        if (!this.isPositiveNumber(value)) {

            return `Length can not be negative number.`;
          }
        }
        break;

      case 'tonnage':    {

        if (!this.isPositiveNumber(value)) {

            return `Tonnage can not be negative number.`;
          }

        }
        break;

      case 'priority': {

          if (!this.isPositiveNumber(value)) {

            return `Priority can not be negative number.`;
          }
        }
        break;

      case 'overtake': {

        if (!this.isPositiveNumber(value)) {

            return `Max Overtake can not be negative number.`;
          }
        }
        break;

      case 'src': {

          if ( !(value instanceof Track)) {

            return `Starting point is not a track.`;
          }

          return this.checkDirection();
        }

      case 'dst': {

          if ( !(value instanceof Track)) {

            return `Destination is not a track.`;
          }

          return this.checkDirection();
        }

    }

    /* no obvious error */

    return '';
  }

  /**
   * helper to do validation for the given fields and values.  The 3rd column is for which columns
   * to tag as invalid when the given column is invalid. Returns current validity status for train.
   */
  protected validateValues(values: [string, any, string][]) {

    values.forEach((datum: [string, any, string]) => {

      const errMsg: string = this.checkValue(datum[0], datum[1]);

      /* before we do error processing, check for suppression or conversion to warnings */

      if (this.suppressible(datum[0], errMsg)) {

        /* this error is suppressible, or considered to be less critical (a warning) */

        this.warning(datum[2], errMsg);

        return ;
      }

      if (errMsg === '') {

        /* no error */

        return ;
      }

      /* track the error */

      this.error(datum[2], errMsg);
    });

    return this.isValid();
  }

  /** reset the list of invalid fields, based on current values */
  public validate(): boolean {

    this._invalidFields = [];
    this._errors        = [];

    /* basic checks on common fields ... */

    this.validateValues([
      ['name',       this._name,        'name'],
      ['trainclass', this._tc,          'trainclass,src,dst'],
      ['length',     this._length,      'length'],
      ['tonnage',    this._tonnage,     'tonnage'],
      ['priority',   this._priority,    'priority'],
      ['overtake',   this._overtake,    'overtake'],
      ['src',        this._paths[0][0], 'trainclass,src,dst'],
      ['dst',        this._paths[0][1], 'trainclass,src,dst']
    ]);

    this.checkDirection();

    /* if nothing is invalid, we're good! */

    return this.isValid();
  }

  /** helper to record less critical or suppressible errors (warnings) */
  public warning(fields: string|string[], text: string): void {

    this._warnings.push(`${text}`);

    /* mark the field(s) as invalid (if not already marked) */

    if (!Array.isArray(fields)) {

      fields = fields.split(/\s*,\s*/);
    }

    fields.forEach((field: string) => {

      if (!this._warningFields.includes(field)) {

        this._warningFields.push(field);
      }

    });

  }

  /** helper to record an additional validation error */
  public error(fields: string|string[], text: string): void {

    /* log the error */

    this._errors.push(`${text}`);

    /* mark the field(s) as invalid (if not already marked) */

    if (!Array.isArray(fields)) {

      fields = fields.split(/\s*,\s*/);
    }

    fields.forEach((field: string) => {

      if (!this._invalidFields.includes(field)) {

        this._invalidFields.push(field);
      }

    });

  }

  /** check if this field is currently valid */
  public isFieldValid(field: string): boolean {

    let f: string|null = this.normalizeFieldName(field);

    if (!f) {

      return false;
    }

    return this._invalidFields.includes(f);
  }

  /** check to see if this train is currently valid. */
  public isValid(): boolean {

    return this._invalidFields.length === 0;
  }
}
